import React, { useState } from 'react';
import {
    Phone,
    Search,
    User,
    MapPin,
    CreditCard,
    AlertCircle,
    Copy,
    Check,
    Eye,
    Mail,
    X,
} from 'lucide-react';
import { TextField, InputAdornment, IconButton, CircularProgress, Skeleton, Tooltip } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import Calling from '../../api/Calling';
import { toast } from "react-toastify";

const Mobile = () => {
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [activeSearch, setActiveSearch] = useState('');
    const [error, setError] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [viewRecord, setViewRecord] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);

    // Search function
    const handleSearch = async () => {
        const query = searchInput.trim();
        if (!query) {
            toast.warning("Please enter a search term", { autoClose: 2000 });
            return;
        }

        setLoading(true);
        setError(null);
        setActiveSearch(query);
        setHasSearched(true);

        try {
            const resp = await Calling.adminSearch({ mobile: query });
            if (resp?.status) {
                setRecord(resp.data || null);
            } else {
                setError(resp?.message || 'Failed to fetch record');
                setRecord(null);
            }
        } catch (err) {
            setError('Something went wrong');
            setRecord(null);
        }
        setLoading(false);
    };

    // Handle Enter key
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Clear search
    const handleClear = () => {
        setSearchInput('');
        setActiveSearch('');
        setHasSearched(false);
        setRecord(null);
        setError(null);
    };

    // Copy to clipboard
    const handleCopy = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        toast.success("Copied!", { autoClose: 1000 });
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Get operator color
    const getCircleColor = (circle) => {
        if (circle?.includes('JIO')) return 'bg-[#e8f4fc] text-[#007bff] border-[#007bff]/30';
        if (circle?.includes('AIRTEL')) return 'bg-[#fef2f2] text-[#F34235] border-[#F34235]/30';
        if (circle?.includes('VI')) return 'bg-[#fffbeb] text-[#d97706] border-[#FFC107]/50';
        if (circle?.includes('BSNL')) return 'bg-main-th text-main-blue border-main-blue/30';
        return 'bg-main-bg text-main-subfont border-main-border';
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full min-h-screen bg-main-bg rounded-2xl"
        >
            <div className="p-4">

                {/* Header Card */}
                <div className="bg-main-white rounded-2xl border border-main-border shadow-sm p-5 md:p-6 mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Title */}
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-main-th rounded-xl flex items-center justify-center border border-main-blue/20">
                                <Phone className="text-main-blue" size={22} />
                            </div>
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold text-main-font">Mobile Records</h1>
                                <p className="text-sm text-main-subfont mt-0.5">Search mobile number database</p>
                            </div>
                        </div>

                        {/* Search Box */}
                        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                            <div className="flex-1 lg:w-80">
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="text"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter mobile number..."
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", backgroundColor: "#fff" } }}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search size={18} className="text-main-subfont" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searchInput ? (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Clear">
                                                        <IconButton
                                                            onClick={() => setSearchInput('')}
                                                            size="small"
                                                            edge="end"
                                                        >
                                                            <X size={16} className="text-main-subfont" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </InputAdornment>
                                            ) : null,
                                        },
                                    }}
                                />
                            </div>
                            <motion.button
                                whileTap={{ scale: 0.96 }}
                                onClick={handleSearch}
                                disabled={loading || !searchInput.trim()}
                                className="flex items-center justify-center gap-2 px-6 py-3 bg-main-blue text-main-white rounded-xl hover:bg-[#2db84d] transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-main-blue/25"
                            >
                                {loading ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <Search size={18} />
                                )}
                                Search
                            </motion.button>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="bg-main-white rounded-2xl border border-main-border shadow-sm overflow-hidden">

                    {/* Active Search Bar */}
                    {hasSearched && activeSearch && (
                        <div className="px-5 py-4 border-b border-main-border bg-main-th/50">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm text-main-subfont">Showing results for:</span>
                                    <span className="px-3 py-1.5 bg-main-blue/10 text-main-blue text-sm font-semibold rounded-lg border border-main-blue/20">
                                        {activeSearch}
                                    </span>
                                </div>
                                <button
                                    onClick={handleClear}
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-main-subfont hover:text-main-delete hover:bg-main-delete/10 rounded-lg transition-all font-medium"
                                >
                                    <X size={16} />
                                    Clear
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="p-5 md:p-6">

                        {/* Error */}
                        {error && (
                            <div className="flex items-center gap-3 p-4 mb-5 rounded-xl bg-main-delete/10 border border-main-delete/30">
                                <div className="w-10 h-10 rounded-full bg-main-delete/20 flex items-center justify-center flex-shrink-0">
                                    <AlertCircle className="text-main-delete" size={20} />
                                </div>
                                <p className="text-main-delete text-sm font-medium">{error}</p>
                            </div>
                        )}

                        {/* Initial State */}
                        {!hasSearched && !loading && (
                            <div className="text-center py-16 md:py-20">
                                <div className="w-24 h-24 md:w-28 md:h-28 bg-main-th rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-main-blue/20">
                                    <Search className="text-main-blue" size={48} />
                                </div>
                                <h2 className="text-xl md:text-2xl font-bold text-main-font">Search Mobile Records</h2>
                                <p className="text-main-subfont mt-2 max-w-sm mx-auto text-sm md:text-base">
                                    Enter a mobile number to search and view details from the database
                                </p>
                            </div>
                        )}

                        {/* Loading */}
                        {loading && (
                            <div className="bg-main-bg rounded-xl p-5 border border-main-border">
                                <div className="flex items-start gap-4">
                                    <Skeleton variant="circular" animation="wave" width={56} height={56} className="flex-shrink-0" />
                                    <div className="flex-1 py-1">
                                        <Skeleton variant="text" animation="wave" height={24} width="40%" sx={{ borderRadius: '8px' }} />
                                        <Skeleton variant="text" animation="wave" height={20} width="25%" sx={{ borderRadius: '8px' }} />
                                        <Skeleton variant="text" animation="wave" height={20} width="60%" sx={{ borderRadius: '8px' }} />
                                    </div>
                                </div>
                                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Skeleton variant="rounded" animation="wave" height={64} sx={{ borderRadius: '12px' }} />
                                    <Skeleton variant="rounded" animation="wave" height={64} sx={{ borderRadius: '12px' }} />
                                </div>
                            </div>
                        )}

                        {/* No Results */}
                        {hasSearched && !loading && !record && !error && (
                            <div className="text-center py-14 md:py-16">
                                <div className="w-20 h-20 bg-main-bg rounded-full flex items-center justify-center mx-auto mb-5 border-2 border-dashed border-main-border">
                                    <Phone className="text-main-subfont" size={32} />
                                </div>
                                <h3 className="text-lg font-bold text-main-font">No Results Found</h3>
                                <p className="text-main-subfont mt-2 text-sm">
                                    No records found for "<span className="font-semibold text-main-font">{activeSearch}</span>"
                                </p>
                                <button
                                    onClick={handleClear}
                                    className="mt-5 px-6 py-2.5 text-main-blue hover:bg-main-th rounded-xl font-semibold transition-all text-sm border border-main-blue/20"
                                >
                                    Try Another Search
                                </button>
                            </div>
                        )}

                        {/* Result */}
                        {hasSearched && !loading && record && (
                            <div className="rounded-xl border border-main-border bg-main-bg overflow-hidden hover:border-main-blue/40 transition-all">
                                {/* Result Header */}
                                <div className="p-4 md:p-5 border-b border-main-border/50 bg-main-white">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 bg-main-th rounded-full flex items-center justify-center border-2 border-main-blue/20 flex-shrink-0">
                                                <span className="text-main-blue font-bold text-xl">
                                                    {record.m_name?.charAt(0)?.toUpperCase() || 'M'}
                                                </span>
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-lg text-main-font">{record.m_name || 'Unknown'}</h3>
                                                {record.m_fname && (
                                                    <p className="text-sm text-main-subfont flex items-center gap-1.5 mt-0.5">
                                                        <User size={14} />
                                                        {record.m_fname}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${getCircleColor(record.m_circle)}`}>
                                                {record.m_circle || 'Unknown'}
                                            </span>
                                            {record.m_uid && (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-main-white rounded-lg text-xs font-mono border border-main-border text-main-font">
                                                    <CreditCard size={14} className="text-main-subfont" />
                                                    {record.m_uid}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Result Body */}
                                <div className="p-4 md:p-5 space-y-4">
                                    {/* Contact Info */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {/* Main Number */}
                                        <button
                                            onClick={() => handleCopy(record.m_number, `${record._id}-main`)}
                                            className="flex items-center gap-3 bg-main-white px-4 py-3 rounded-xl border border-main-border hover:border-main-blue/40 hover:bg-main-th/30 transition-all group"
                                        >
                                            <div className="w-10 h-10 bg-main-th rounded-lg flex items-center justify-center border border-main-blue/20 flex-shrink-0">
                                                <Phone size={20} className="text-main-blue" />
                                            </div>
                                            <div className="flex-1 text-left">
                                                <span className="text-xs text-main-subfont block">Primary</span>
                                                <span className="text-sm font-semibold text-main-font font-mono">{record.m_number}</span>
                                            </div>
                                            {copiedId === `${record._id}-main` ? (
                                                <Check size={20} className="text-main-blue" />
                                            ) : (
                                                <Copy size={20} className="text-main-subfont group-hover:text-main-blue transition-colors" />
                                            )}
                                        </button>

                                        {/* Alt Number */}
                                        {record.m_alt_number && (
                                            <button
                                                onClick={() => handleCopy(record.m_alt_number, `${record._id}-alt`)}
                                                className="flex items-center gap-3 bg-main-white px-4 py-3 rounded-xl border border-main-border hover:border-main-blue/40 hover:bg-main-th/30 transition-all group"
                                            >
                                                <div className="w-10 h-10 bg-main-bg rounded-lg flex items-center justify-center border border-main-border flex-shrink-0">
                                                    <Phone size={20} className="text-main-subfont" />
                                                </div>
                                                <div className="flex-1 text-left">
                                                    <span className="text-xs text-main-subfont block">Alternate</span>
                                                    <span className="text-sm font-semibold text-main-font font-mono">{record.m_alt_number}</span>
                                                </div>
                                                {copiedId === `${record._id}-alt` ? (
                                                    <Check size={20} className="text-main-blue" />
                                                ) : (
                                                    <Copy size={20} className="text-main-subfont group-hover:text-main-blue transition-colors" />
                                                )}
                                            </button>
                                        )}
                                    </div>

                                    {/* Email */}
                                    {record.m_email && (
                                        <button
                                            onClick={() => handleCopy(record.m_email, `${record._id}-email`)}
                                            className="w-full flex items-center gap-3 bg-main-white px-4 py-3 rounded-xl border border-main-border hover:border-main-blue/40 hover:bg-main-th/30 transition-all group"
                                        >
                                            <div className="w-10 h-10 bg-main-th rounded-lg flex items-center justify-center border border-main-blue/20 flex-shrink-0">
                                                <Mail size={20} className="text-main-blue" />
                                            </div>
                                            <div className="flex-1 text-left min-w-0">
                                                <span className="text-xs text-main-subfont block">Email</span>
                                                <span className="text-sm font-medium text-main-font truncate block">{record.m_email}</span>
                                            </div>
                                            {copiedId === `${record._id}-email` ? (
                                                <Check size={20} className="text-main-blue flex-shrink-0" />
                                            ) : (
                                                <Copy size={20} className="text-main-subfont group-hover:text-main-blue transition-colors flex-shrink-0" />
                                            )}
                                        </button>
                                    )}

                                    {/* Address */}
                                    {record.m_address && (
                                        <div className="flex items-start gap-3 bg-main-white px-4 py-3 rounded-xl border border-main-border">
                                            <div className="w-10 h-10 bg-main-bg rounded-lg flex items-center justify-center border border-main-border flex-shrink-0 mt-0.5">
                                                <MapPin size={20} className="text-main-subfont" />
                                            </div>
                                            <div className="flex-1">
                                                <span className="text-xs text-main-subfont block">Address</span>
                                                <span className="text-sm text-main-font leading-relaxed">{record.m_address}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Result Footer */}
                                <div className="px-4 md:px-5 py-3 border-t border-main-border/50 bg-main-white flex items-center justify-end">
                                    <button
                                        onClick={() => setViewRecord(record)}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm text-main-blue hover:bg-main-th rounded-lg font-semibold transition-all border border-main-blue/20"
                                    >
                                        <Eye size={16} />
                                        View All Details
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* View Modal */}
            <AnimatePresence>
                {viewRecord && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        onClick={() => setViewRecord(null)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            className="bg-main-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-main-white border-b border-main-border px-5 py-4 flex items-center justify-between">
                                <h3 className="font-bold text-lg text-main-font">Record Details</h3>
                                <button
                                    onClick={() => setViewRecord(null)}
                                    className="w-9 h-9 rounded-xl bg-main-bg hover:bg-main-delete/10 flex items-center justify-center text-main-subfont hover:text-main-delete transition-all"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="overflow-y-auto max-h-[calc(90vh-65px)]">
                                <div className="p-5 space-y-5">
                                    {/* Profile Header */}
                                    <div className="flex items-center gap-4 p-4 bg-main-th/50 rounded-xl border border-main-blue/20">
                                        <div className="w-16 h-16 bg-main-th rounded-full flex items-center justify-center border-2 border-main-blue/30">
                                            <span className="text-main-blue font-bold text-2xl">{viewRecord.m_name?.charAt(0)?.toUpperCase() || 'M'}</span>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg text-main-font">{viewRecord.m_name}</h4>
                                            <span className={`inline-block mt-1.5 px-3 py-1 rounded-lg text-xs font-semibold border ${getCircleColor(viewRecord.m_circle)}`}>
                                                {viewRecord.m_circle}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Details Grid */}
                                    <div className="space-y-3">
                                        <DetailRow label="Mobile Number" value={viewRecord.m_number} icon={<Phone size={18} />} copyable onCopy={handleCopy} id="modal-main" copiedId={copiedId} />
                                        {viewRecord.m_alt_number && <DetailRow label="Alternate Number" value={viewRecord.m_alt_number} icon={<Phone size={18} />} copyable onCopy={handleCopy} id="modal-alt" copiedId={copiedId} />}
                                        {viewRecord.m_email && <DetailRow label="Email Address" value={viewRecord.m_email} icon={<Mail size={18} />} copyable onCopy={handleCopy} id="modal-email" copiedId={copiedId} />}
                                        {viewRecord.m_fname && <DetailRow label="Father's Name" value={viewRecord.m_fname} icon={<User size={18} />} />}
                                        {viewRecord.m_uid && <DetailRow label="UID / PAN" value={viewRecord.m_uid} icon={<CreditCard size={18} />} copyable onCopy={handleCopy} id="modal-uid" copiedId={copiedId} />}
                                        {viewRecord.m_address && <DetailRow label="Address" value={viewRecord.m_address} icon={<MapPin size={18} />} />}
                                    </div>

                                    {/* Sub Names */}
                                    {viewRecord.m_sub_name && viewRecord.m_sub_name.length > 0 && (
                                        <div className="pt-4 border-t border-main-border">
                                            <span className="text-xs font-semibold text-main-subfont uppercase tracking-wider block mb-3">Associated Names</span>
                                            <div className="flex flex-wrap gap-2">
                                                {viewRecord.m_sub_name.map((name, idx) => (
                                                    <span key={idx} className="px-3 py-2 bg-main-th rounded-lg text-sm font-medium text-main-font border border-main-blue/20">
                                                        {name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

// Detail Row Component
const DetailRow = ({ label, value, icon, copyable, onCopy, id, copiedId }) => (
    <div className="flex items-start gap-3 p-3.5 bg-main-bg rounded-xl border border-main-border hover:border-main-blue/30 transition-all">
        <div className="w-10 h-10 bg-main-th rounded-lg flex items-center justify-center text-main-blue flex-shrink-0 border border-main-blue/20">
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <span className="text-xs text-main-subfont font-medium">{label}</span>
            <div className="flex items-center gap-2 mt-1">
                <span className="text-sm font-semibold text-main-font break-all">{value}</span>
                {copyable && (
                    <button
                        onClick={() => onCopy(value, id)}
                        className="text-main-subfont hover:text-main-blue transition-all flex-shrink-0 p-1 rounded hover:bg-main-th"
                    >
                        {copiedId === id ? <Check size={16} className="text-main-blue" /> : <Copy size={16} />}
                    </button>
                )}
            </div>
        </div>
    </div>
);

export default Mobile;
